'use client';

import { Alert, AlertColor, Snackbar } from '@mui/material';
import { useEffect, useState } from 'react';
import { useParams } from 'next/navigation';
import { getCookieSettings, saveCookieSettings } from './actions';
import CookiesEn from './CookiesEn';
import CookiesZhCN from './CookiesZhCN';
import CookiesZhTW from './CookiesZhTW';
import { localStorageGetCookieSettings, localStorageSaveCookieSettings } from '@/app/components/CookieSetting';


export default function Cookies() {
  const [settings, setSettings] = useState<CookieSettings>({
    performance: true,
    functional: true,
    advertising: true
  });

  const [notification, setNotification] = useState({
    open: false,
    message: '',
    severity: 'success' as AlertColor
  });

  useEffect(() => {
    const loadSettings = async () => {
      try {
        // 先尝试从服务器获取设置
        const serverData = await getCookieSettings();

        // 如果服务器获取失败，尝试从本地存储获取
        if (!serverData) {
          const localData = localStorageGetCookieSettings();
          if (localData) {
            setSettings(localData);
            return;
          }
        }

        if (serverData) {
          setSettings(serverData);
        }
      } catch (error) {
        // 如果服务器请求失败，尝试从本地存储获取
        const localData = localStorageGetCookieSettings();
        if (localData) {
          setSettings(localData);
        }
        console.error('Error fetching cookie settings:', error);
      }
    };

    loadSettings();
  }, []);

  const handleCookieChange = (type: string, value: boolean) => {
    setSettings(prev => ({
      ...prev,
      [type]: value
    }));
  };

  const handleSaveSettings = async () => {
    try {
      await saveCookieSettings(settings);
      // 同时保存到本地存储
      localStorageSaveCookieSettings(settings);
      setNotification({
        open: true,
        message: 'Cookie settings saved successfully',
        severity: 'success'
      });
    } catch (error) {
      // 如果服务器保存失败，至少保存到本地
      localStorageSaveCookieSettings(settings);
      setNotification({
        open: true,
        message: 'Settings saved locally',
        severity: 'success'
      });
    }
  };

  const handleRejectAll = async () => {
    const rejectedSettings = {
      performance: false,
      functional: false,
      advertising: false
    };
    setSettings(rejectedSettings);
    try {
      await saveCookieSettings(rejectedSettings);
      // 同时保存到本地存储
      localStorageSaveCookieSettings(settings);
      setNotification({
        open: true,
        message: 'All optional cookies rejected',
        severity: 'success'
      });
    } catch (error) {
      // 如果服务器保存失败，至少保存到本地
      localStorageSaveCookieSettings(settings);
      setNotification({
        open: true,
        message: 'Settings saved locally',
        severity: 'success'
      });
    }
  };

  const handleCloseNotification = () => {
    setNotification(prev => ({ ...prev, open: false }));
  };

  const notificationComp = (<Snackbar
    open={notification.open}
    autoHideDuration={3000}
    onClose={handleCloseNotification}
    anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
    sx={{
      '& .MuiSnackbarContent-root': {
        minWidth: '300px',
      }
    }}
  >
    <Alert
      onClose={handleCloseNotification}
      severity={notification.severity}
      sx={{
        width: '100%',
        boxShadow: 3
      }}
    >
      {notification.message}
    </Alert>
  </Snackbar>);

  const params = useParams();
  const locale = params.locale as string;

  switch (locale) {
    case 'zh-CN':
      return <CookiesZhCN
        settings={settings}
        handleCookieChange={handleCookieChange}
        handleSaveSettings={handleSaveSettings}
        handleRejectAll={handleRejectAll}
        notification={notificationComp}
      />;
    case 'zh-TW':
      return <CookiesZhTW
        settings={settings}
        handleCookieChange={handleCookieChange}
        handleSaveSettings={handleSaveSettings}
        handleRejectAll={handleRejectAll}
        notification={notificationComp}
      />;
    default:
      return <CookiesEn
        settings={settings}
        handleCookieChange={handleCookieChange}
        handleSaveSettings={handleSaveSettings}
        handleRejectAll={handleRejectAll}
        notification={notificationComp}
      />;
  }
}



